// ResultPanel.jsx (frontend copy)
// Contextualized "Analysis Results" view rendering structured fields + Save button.
import React from 'react';
import JSONViewer from '../ui/JSONViewer';

const cn = (...c) => c.filter(Boolean).join(' ');
const Button = ({ className='', variant='outline', disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={cn('inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2','border-default/50 bg-surface hover:bg-elevated/60 disabled:opacity-50 disabled:cursor-not-allowed', variant === 'primary' && 'bg-[var(--lodia-brand,#2563eb)] text-white hover:opacity-90 border-black/20 shadow-md', className)}
  />
);
const Card = ({ className='', ...rest }) => <div className={cn('rounded-2xl border bg-elevated/30 p-6 shadow-sm', className)} {...rest} />;
const Separator = ({ className='' }) => <div className={cn('my-4 h-px w-full bg-border/40', className)} />;

function Badge({ children, tone='neutral' }) {
  const toneMap = { neutral: 'border-default/50 text-default', success: 'border-emerald-500/30 text-emerald-700 bg-emerald-500/10', info: 'border-sky-500/30 text-sky-700 bg-sky-500/10', warn: 'border-amber-500/30 text-amber-700 bg-amber-500/10' };
  return <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs', toneMap[tone] || toneMap.neutral)}>{children}</span>;
}
function Field({ label, value, tone='neutral' }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
  <span className="text-sm text-black">{label}</span>
      <Badge tone={tone}><span className="capitalize">{value ?? '—'}</span></Badge>
    </div>
  );
}
function deriveSpec(result, raw) {
  const base = result || {}; const r = raw || base.raw || {}; const parsed = r.parsed || r; const pick = (k) => base[k] ?? parsed[k] ?? null; const norm = (v) => typeof v === 'string' ? v.trim() : (v == null ? null : String(v));
  const material = norm(pick('material')); const lasttyp = norm(pick('lasttyp')); const forpackning = norm(pick('forpackning')); const stapelbarhet = norm(pick('stapelbarhet')); const trycktalighet = norm(pick('trycktalighet')); const viktkategori = norm(pick('viktkategori')); const omtalighet = norm(pick('omtalighet')); const form = norm(pick('form')); const recommended_action = pick('recommended_action'); const training_tip = pick('training_tip');
  const toneForBool = (val) => { const v = (val || '').toString().toLowerCase(); if (v === 'ja' || v === 'true' || v === 'yes') return 'success'; if (v === 'nej' || v === 'false' || v === 'no') return 'neutral'; return 'neutral'; };
  const toneForLevel = (val) => { const v = (val || '').toString().toLowerCase(); if (v === 'hög' || v === 'hog' || v === 'high') return 'success'; if (v === 'medel' || v === 'medium') return 'warn'; return 'neutral'; };
  return { label: base.label || parsed.label || null, confidence: base.confidence ?? parsed.confidence ?? null, model: base.model || base.modelVersion || parsed.model || null, inference_ms: base.inference_ms ?? parsed.inference_ms ?? null, material, lasttyp, stapelbarhet, trycktalighet, viktkategori, omtalighet, forpackning, form, recommended_action, training_tip, tones: { stapelbarhet: toneForBool(stapelbarhet), trycktalighet: toneForLevel(trycktalighet), viktkategori: toneForLevel(viktkategori), omtalighet: toneForBool(omtalighet) } };
}

export function ResultPanel({ result, raw, onSave, saving, canSave, formatConfidence }) {
  const spec = deriveSpec(result, raw);
  const SHOW_TECHNICAL = false; // hide from users for now; flip to true to restore
  return (
    <Card aria-label="Analysis Results">
      <h2 className="text-lg font-semibold">Analysis Results</h2>
  <p className="mt-1 text-xs text-black">AI-powered pallet classification and specifications</p>
      <Separator />
  {!result && (<p className="text-sm text-black">No classification yet. Run a classification to see results.</p>)}
      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            <Field label="Material" value={spec.material} />
            <Field label="Lasttyp" value={spec.lasttyp} />
            <Field label="Stapelbarhet" value={spec.stapelbarhet} tone={spec.tones.stapelbarhet} />
            <Field label="Trycktålighet" value={spec.trycktalighet} tone={spec.tones.trycktalighet} />
            <Field label="Viktkategori" value={spec.viktkategori} tone={spec.tones.viktkategori} />
            <Field label="Ömtålig" value={spec.omtalighet} tone={spec.tones.omtalighet} />
            <Field label="Förpackning" value={spec.forpackning} />
            <Field label="Form" value={spec.form} />
          </div>
          {(spec.recommended_action || spec.training_tip) && (
            <div className="space-y-3">
              {spec.recommended_action && (
                <div className="rounded-xl border bg-sky-50 p-3 text-sm">
                  <div className="mb-1 font-medium text-black">Rekommenderad hantering</div>
                  <p className="text-black">{spec.recommended_action}</p>
                </div>
              )}
              {spec.training_tip && (
                <div className="rounded-xl border bg-amber-50 p-3 text-sm">
                  <div className="mb-1 font-medium text-black">Utbildningstips</div>
                  <p className="text-black">{spec.training_tip}</p>
                </div>
              )}
            </div>
          )}
          {SHOW_TECHNICAL && (
            <details className="mt-2">
              <summary className="cursor-pointer select-none text-xs text-black">Technical details</summary>
              <div className="mt-2 space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-black block">Top Label</span><span className="font-medium">{spec.label ?? '—'}</span></div>
                  <div><span className="text-black block">Confidence</span><span className="font-medium">{spec.confidence != null ? formatConfidence(Number(spec.confidence)) : '—'}</span></div>
                  <div><span className="text-black block">Model</span><span className="font-medium">{spec.model ?? '—'}</span></div>
                  <div><span className="text-black block">Inference Time</span><span className="font-medium">{spec.inference_ms != null ? `${spec.inference_ms} ms` : '—'}</span></div>
                </div>
                <div>
                  <h3 className="mb-1 text-xs font-medium">Raw JSON</h3>
                  <JSONViewer value={raw || result?.raw || {}} />
                </div>
              </div>
            </details>
          )}
        </div>
      )}
      <Separator />
      <Button
        data-testid="save-button"
        variant="primary"
        disabled={!canSave || saving}
        onClick={onSave}
        aria-disabled={!canSave || saving}
        aria-busy={saving}
      >
        {saving ? 'Saving...' : 'Save to History'}
      </Button>
    </Card>
  );
}

export default ResultPanel;
